import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, vModelCheckbox as _vModelCheckbox, createElementVNode as _createElementVNode, withDirectives as _withDirectives, createVNode as _createVNode, normalizeClass as _normalizeClass, withCtx as _withCtx, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, Fragment as _Fragment, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "live-auction-action-buttons"
}
const _hoisted_2 = {
  key: 0,
  class: "switch-block-bid-button-mob"
}
const _hoisted_3 = { class: "switch" }
const _hoisted_4 = { class: "locked-button-label" }
const _hoisted_5 = {
  key: 1,
  class: "label-locked-btn btn-lock-bidding"
}
const _hoisted_6 = { class: "bid-value" }
const _hoisted_7 = ["disabled"]
const _hoisted_8 = { class: "text" }
const _hoisted_9 = ["disabled"]
const _hoisted_10 = { class: "text" }
const _hoisted_11 = ["disabled"]
const _hoisted_12 = { class: "text" }
const _hoisted_13 = ["disabled"]
const _hoisted_14 = { class: "text" }

import { computed, watch, ref, onMounted, markRaw, inject, reactive } from "vue";
  import Api from "@/services/api";
  import { useToast } from "primevue/usetoast";
  import { useAuctionStore } from "@/stores/AuctionStore";
  import { format_currency } from "@/utils/currencies";
  import { trans } from "@/utils/trans";
  import { IPopupError } from "@/models";
  import { AuctionStatuses, LotStatuses, BiddingSteps, LotSoldStatuses } from "@/enum";
  import { BaseIcon, DashboardIcon, LockIcon, UnlockIcon } from "@/components/icons";
  import Popup from "@/components/_partials/Popup.vue";
  import DashboardPopup from "@/components/_partials/DashboardPopup.vue";
  import { isScreenL } from "@/utils/media-size";

  const COLOR_NONE = "none";
  const COLOR_LIME = "#E0FFF7";
  const COLOR_GREY = "#F7F8FA";
  
export default /*#__PURE__*/_defineComponent({
  __name: 'ActionButtons',
  setup(__props) {

  const toast = useToast();
  const popup = ref<typeof Popup>();
  const component = markRaw(DashboardPopup);
  const emitter = inject("emitter");

  const auctionStore = useAuctionStore();
  const auction = computed(() => auctionStore.auction);
  const lots = computed(() => auctionStore.lots);
  const preparedLots = computed(() => lots.value.filter((item) => item.status === LotStatuses.PREPARED));
  const liveLot = computed(() => lots.value.find((item) => item.status === LotStatuses.LIVE));
  const endedLots = computed(() => lots.value.filter((item) => item.status === LotStatuses.ENDED));
  const isAuctionStarted = computed(() => liveLot.value?.id || endedLots.value.length);
  const auctionStatus = computed(() => auctionStore.auction.status);

  const activeLot = computed(() => auctionStore.activeLot?.id ? auctionStore.activeLot : liveLot.value);
  const currentBid = computed(() => activeLot.value?.current_bid);
  const bidsCount = computed(() => activeLot.value?.bids_count);
  const bookBid = computed(() => activeLot.value?.book_bid);
  const currentStep = computed(() => (auctionStore.activeLot?.id ? auctionStore.activeLot.live_step : 0));
  const liveSettings = computed(() => auctionStore.liveSettings);
  const hasMobileBiddingState = computed(
    () => isScreenL.value && auctionStatus.value === AuctionStatuses.LIVE && lots.value.length > endedLots.value.length && !!liveLot.value?.id
  );
  const showMobileLockToggle = computed(() => hasMobileBiddingState.value);

  const isLotCompleted = computed(
      () =>
          activeLot.value?.sold_status === LotSoldStatuses.SOLD || activeLot.value?.sold_status === LotSoldStatuses.UNSOLD
  );

  const bidVariants = ref(auction.value.bid_variants);
  const bookBidVariantLabel = computed(() => format_currency(bidVariants.value[0]));
  const nextLotLabel = computed(() => {
    if (preparedLots.value.length === 0 && liveLot) {
      return trans("live.action_buttons.close_last_lot_label");
    } else if (auctionStore.liveSettings?.next_lot_label) {
      return auctionStore.liveSettings.next_lot_label;
    } else {
      return trans("live.action_buttons.next_lot_label");
    }
  });

  const liveLotBtnIsLoading = ref(false);

  let interval: number | undefined;
  const remainingTime = ref(0);
  const progressWidth = ref(0);
  const progressColor = ref<string>(COLOR_NONE);

  const waitServer = reactive({
    retractBid: false
  });

  const passLotState = ref<'pass' | 'confirm'>('pass');
  const reopenLotState = ref<'reopen' | 'confirm'>('reopen');

  watch(currentBid, () => {
    refreshBidVariants();
  });

  watch(bidsCount, () => {
    refreshBidVariants();
  });

  watch(bookBid, () => {
    refreshBidVariants();
  });

  watch(
      () => auctionStore.refreshStepButtonTrigger,
      () => {
        fillStepButtonCheck();
      }
  );

  watch(() => auctionStore.liveSettings, () => {
    fillStepButtonCheck();
  }, { deep: true });

  onMounted(() => {
    refreshBidVariants();
    emitterSubscribe();
  });

  const passLot = () => {
    if (passLotState.value === 'pass') {
      passLotState.value = 'confirm';
    } else {
      Api.post("/live/lots/" + activeLot.value?.id + "/pass").catch((error) => {
        console.log(error);
      });

      passLotState.value = 'pass';
    }
  };

  const reopenLot = async () => {
    if (reopenLotState.value === 'reopen') {
      reopenLotState.value = 'confirm';
    } else {
      if (liveLot.value) {
        await auctionStore
          .reopenLot(liveLot.value)
          .catch((error) => {
            console.log(error);
          });
      } else {
        console.log('Live lot is not set!');
      }

      reopenLotState.value = 'reopen';
    }
  };

  const placeBookBid = (amount: number) => {
    if (activeLot.value?.id) {
      Api.post("/live/lots/" + activeLot.value?.id + "/place-bid", {
        amount: amount,
        is_book_bid: true
      })
        .then((response) => {
          if (response.data.success) {
            auctionStore.setActiveLotBookBid(amount);
          }
        })
        .catch((error) => {
          console.log(error);

          const errors = error.response.data.errors;
          Object.keys(errors).forEach((key) => {
            errors[key].forEach((error: IPopupError) => {
              toast.add({
                severity: "error",
                summary: error.title,
                detail: error.text,
                life: 6000
              });
            });
          });
        });
    }
  };

  const handleOnceBtn = async () => {
    if (liveSettings.value?.is_change_bidding_status_auto) {
      toast.add({
        severity: "warn",
        summary: "Operation is blocked",
        detail: "Lot auto-changing of status is enabled",
        life: 6000
      });
    } else {
      await runBiddingStep(BiddingSteps.ONCE);
    }
  };

  const handleTwiceBtn = async () => {
    if (liveSettings.value?.is_change_bidding_status_auto) {
      toast.add({
        severity: "warn",
        summary: "Operation is blocked",
        detail: "Lot auto-changing of status is enabled",
        life: 6000
      });
    } else {
      await runBiddingStep(BiddingSteps.TWICE);
    }
  };

  const handleCompleteLot = async () => {
    if (liveSettings.value?.is_complete_current_lot_auto) {
      toast.add({
        severity: "warn",
        summary: "Operation is blocked",
        detail: "Lot auto-closing is enabled",
        life: 6000
      });
    } else {
      await completeLot();
    }
  };

  const handleNextLotBtn = async () => {
    if (liveSettings.value?.is_open_next_lot_auto) {
      toast.add({
        severity: "warn",
        summary: "Operation is blocked",
        detail: "Auto-opening of the next lot is enabled",
        life: 6000
      });
    } else {
      await nextLot();
    }
  };

  const runBiddingStep = async (step: BiddingSteps) => {
    liveLotBtnIsLoading.value = true;
    await Api.post("/live/lots/" + activeLot.value?.id + "/bidding-step", { step })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        liveLotBtnIsLoading.value = false;
      });
  };

  const showDashboardPopup = () => {
    popup.value?.toggleComponent(component);
    popup.value?.open();
  };

  const toggleLockButtons = () => {
    auctionStore.$patch({
      isLockedButtons: !auctionStore.isLockedButtons
    });
  };

  const completeLot = async () => {
    liveLotBtnIsLoading.value = true;
    await Api.post("/live/lots/" + activeLot.value?.id + "/complete")
      .catch((error) => {
        toast.add({
          severity: "error",
          summary: error.response.data.message.title,
          detail: error.response.data.message.text,
          life: 6000
        });
      })
      .finally(() => {
        liveLotBtnIsLoading.value = false;
      });
  };

  const nextLot = async () => {
    liveLotBtnIsLoading.value = true;
    const nextLot = lots.value.find((item) => item.status === LotStatuses.PREPARED);
    if (nextLot) {
      return await auctionStore.activateLot(nextLot)
        .catch((error) => console.log(error))
        .finally(() => liveLotBtnIsLoading.value = false);
    } else if (liveLot.value) {
      return await auctionStore.closeLot(liveLot.value)
        .catch((error) => console.log(error))
        .finally(() => liveLotBtnIsLoading.value = false);
    }
  };

  const startAuction = async () => {
    if (liveSettings.value?.is_open_next_lot_auto) {
      toast.add({
        severity: "warn",
        summary: "Operation is blocked",
        detail: "Auto-opening of the next lot is enabled",
        life: 6000
      });
    } else {
      await auctionStore.startAuction(auction.value)
        .then(async (response) => {
          if (response.data.success) {
            await auctionStore.fetchAuction();
            auctionStore.fetchLots();
          }
        })
        .catch((error) => {
          console.log(error);
        });
    }
  };

  const continueAuction = async () => {
    await nextLot();
  };

  const endAuction = async () => {
    Api.post("/live/auctions/" + auction.value.id + "/complete")
      .then(async (response) => {
        if (response.data.success) {
          await auctionStore.fetchAuction();
          auctionStore.fetchLots();
        }

        toast.add({
          severity: response.data.success ? "success" : "error",
          summary: response.data.message.title,
          detail: response.data.message.text,
          life: 6000
        });
      })
      .catch((error) => {
        console.log(error);

        const errors = error.response.data.errors;
        Object.keys(errors).forEach((key) => {
          errors[key].forEach((error: IPopupError) => {
            toast.add({
              severity: "error",
              summary: error.title,
              detail: error.text,
              life: 6000
            });
          });
        });
      });
  };

  const fillStepButtonCheck = () => {
    progressColor.value = COLOR_NONE;

    if (
      liveSettings.value?.is_change_bidding_status_auto &&
      [BiddingSteps.NONE, BiddingSteps.ONCE].includes(currentStep.value)
    ) {
      progressColor.value = COLOR_GREY;
      let delay = liveSettings.value?.change_bidding_status_mode || 0;
      fillStepButton(delay);
    }

    if (
      liveSettings.value?.is_complete_current_lot_auto &&
      currentStep.value === BiddingSteps.TWICE
    ) {
      progressColor.value = activeLot.value?.bids_count ? COLOR_LIME : COLOR_GREY;
      let delay = liveSettings.value?.complete_current_lot_mode || 0;
      fillStepButton(delay);
    }

    if (
      liveSettings.value?.is_open_next_lot_auto &&
      currentStep.value === BiddingSteps.COMPLETE_LOT
    ) {
      progressColor.value = COLOR_GREY;
      const delay = liveSettings.value?.open_next_lot_auto_mode || 0;
      fillStepButton(delay);
    }
  };

  const fillStepButton = (duration: number) => {
    if (interval) {
      clearInterval(interval);
      interval = undefined;
    }

    remainingTime.value = duration;
    progressWidth.value = 0;

    const startTime = Date.now();
    interval = setInterval(() => {
      const elapsedTime = (Date.now() - startTime) / 1000;
      remainingTime.value = Math.max(duration - Math.floor(elapsedTime), 0);
      progressWidth.value = Math.max((elapsedTime / duration) * 100, 0);

      if (elapsedTime >= duration) {
        clearInterval(interval);
      }
    }, 100);
  };

  const refreshBidVariants = () => {
    bidVariants.value = auctionStore.filteredBidVariants();
  };

  const retractBid = () => {
    if (waitServer.retractBid) return;

    waitServer.retractBid = true;
    Api.post("/live/lots/" + activeLot.value?.id + "/retract-bid")
      .then((response) => {
        if (response.data.success) {
          console.log("response.data", response.data);
        }
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        waitServer.retractBid = false;
      });
  };

  const emitterSubscribe = () => {
    emitter?.on("hot-key:once-twice-sold", async () => {
      if (!currentStep.value) {
        await handleOnceBtn();
      } else if (currentStep.value === BiddingSteps.ONCE) {
        await handleTwiceBtn();
      } else if (currentStep.value === BiddingSteps.TWICE) {
        await handleCompleteLot();
      }
    });

    emitter?.on("hot-key:book-bid", () => placeBookBid(bidVariants.value[0]));
    emitter?.on("hot-key:book-bid-first", () => null); // todo Igor call book-bid-first
    emitter?.on("hot-key:pass-lot", () => passLot());
    emitter?.on("hot-key:retract-bid", () => retractBid());
    emitter?.on("shortcuts:retract-bid", () => retractBid());
  };

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (lots.value.length > 0)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          (showMobileLockToggle.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                _createElementVNode("label", _hoisted_3, [
                  _withDirectives(_createElementVNode("input", {
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((_unref(auctionStore).isLockedButtons) = $event)),
                    type: "checkbox"
                  }, null, 512), [
                    [_vModelCheckbox, _unref(auctionStore).isLockedButtons]
                  ]),
                  _cache[2] || (_cache[2] = _createElementVNode("span", { class: "slider round" }, null, -1)),
                  _createVNode(_unref(BaseIcon), {
                    "icon-name": "lock",
                    class: _normalizeClass({ show: !_unref(auctionStore).isLockedButtons }),
                    "view-box": "0 0 17.721 23.259"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_unref(LockIcon))
                    ]),
                    _: 1
                  }, 8, ["class"]),
                  _createVNode(_unref(BaseIcon), {
                    "icon-name": "unlock",
                    class: _normalizeClass({ show: _unref(auctionStore).isLockedButtons }),
                    "view-box": "0 0 18 24"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_unref(UnlockIcon))
                    ]),
                    _: 1
                  }, 8, ["class"])
                ]),
                _createElementVNode("span", _hoisted_4, _toDisplayString(_unref(auctionStore).isLockedButtons ? "unlocked" : "locked"), 1)
              ]))
            : _createCommentVNode("", true),
          (auctionStatus.value === _unref(AuctionStatuses).LIVE && lots.value.length > endedLots.value.length)
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                (activeLot.value?.id)
                  ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                      _createElementVNode("button", {
                        class: "dashboard-button btn btn-rounded btn-blue-outline btn-click-effect can-toggle-on-mob",
                        onClick: showDashboardPopup
                      }, [
                        _createVNode(_unref(BaseIcon), {
                          class: "dashboard",
                          "icon-name": "dashboard",
                          width: "20",
                          height: "20"
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_unref(DashboardIcon))
                          ]),
                          _: 1
                        })
                      ]),
                      (_unref(auctionStore).isLockedButtons)
                        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                            (!_unref(isScreenL))
                              ? (_openBlock(), _createElementBlock("button", {
                                  key: 0,
                                  class: "btn btn-lock-bidding locked btn-blue-outline",
                                  onClick: toggleLockButtons
                                }, [
                                  _createTextVNode(_toDisplayString(_unref(trans)("live.common.buttons.unlock_buttons")) + " ", 1),
                                  _createVNode(_unref(BaseIcon), {
                                    "icon-name": "lock",
                                    "view-box": "0 0 17.721 23.259"
                                  }, {
                                    default: _withCtx(() => [
                                      _createVNode(_unref(LockIcon))
                                    ]),
                                    _: 1
                                  })
                                ]))
                              : (_openBlock(), _createElementBlock("div", _hoisted_5, _toDisplayString(_unref(trans)("live.bidding.bidding_locked")), 1))
                          ], 64))
                        : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                            (isLotCompleted.value)
                              ? (_openBlock(), _createElementBlock("button", {
                                  key: 0,
                                  class: "pass-lot-button btn btn-rounded btn-blue-outline btn-click-effect can-toggle-on-mob",
                                  onClick: reopenLot
                                }, _toDisplayString(reopenLotState.value === 'reopen'
                ? _unref(trans)('live.action_buttons.reopen_lot_label')
                : _unref(trans)('live.action_buttons.confirm_reopen_lot_label')), 1))
                              : (_openBlock(), _createElementBlock("button", {
                                  key: 1,
                                  class: "pass-lot-button btn btn-rounded btn-blue-outline btn-click-effect can-toggle-on-mob",
                                  onClick: passLot
                                }, _toDisplayString(passLotState.value === 'pass'
                ? _unref(trans)('live.action_buttons.pass_lot_label')
                : _unref(trans)('live.action_buttons.confirm_pass_lot_label')), 1)),
                            _createElementVNode("button", {
                              class: "book-bid-button btn btn-rounded btn-blue btn-click-effect can-toggle-on-mob",
                              onClick: _cache[1] || (_cache[1] = ($event: any) => (placeBookBid(bidVariants.value[0])))
                            }, [
                              _cache[3] || (_cache[3] = _createElementVNode("span", { class: "first-line" }, "Book Bid", -1)),
                              _createElementVNode("span", _hoisted_6, _toDisplayString(bookBidVariantLabel.value), 1)
                            ]),
                            (!currentStep.value)
                              ? (_openBlock(), _createElementBlock("button", {
                                  key: 2,
                                  class: _normalizeClass([
              'live-lot-button btn btn-rounded btn-blue-outline btn-click-effect',
              { disabled: liveLotBtnIsLoading.value || liveSettings.value?.is_change_bidding_status_auto }
            ]),
                                  disabled: liveLotBtnIsLoading.value || liveSettings.value?.is_change_bidding_status_auto,
                                  onClick: handleOnceBtn
                                }, [
                                  _createElementVNode("span", _hoisted_8, _toDisplayString(liveSettings.value?.once_label ?? _unref(trans)("live.action_buttons.once_label")), 1),
                                  (liveSettings.value?.is_change_bidding_status_auto)
                                    ? (_openBlock(), _createElementBlock("span", {
                                        key: 0,
                                        class: "progress-bar",
                                        style: _normalizeStyle({ width: progressWidth.value + '%', backgroundColor: progressColor.value })
                                      }, null, 4))
                                    : _createCommentVNode("", true)
                                ], 10, _hoisted_7))
                              : _createCommentVNode("", true),
                            (currentStep.value === _unref(BiddingSteps).ONCE)
                              ? (_openBlock(), _createElementBlock("button", {
                                  key: 3,
                                  class: _normalizeClass([
              'live-lot-button btn btn-rounded btn-blue-outline btn-click-effect',
              { disabled: liveLotBtnIsLoading.value || liveSettings.value?.is_change_bidding_status_auto }
            ]),
                                  disabled: liveLotBtnIsLoading.value || liveSettings.value?.is_change_bidding_status_auto,
                                  onClick: handleTwiceBtn
                                }, [
                                  _createElementVNode("span", _hoisted_10, _toDisplayString(liveSettings.value?.twice_label ?? _unref(trans)("live.action_buttons.twice_label")), 1),
                                  (liveSettings.value?.is_change_bidding_status_auto)
                                    ? (_openBlock(), _createElementBlock("span", {
                                        key: 0,
                                        class: "progress-bar",
                                        style: _normalizeStyle({ width: progressWidth.value + '%', backgroundColor: progressColor.value })
                                      }, null, 4))
                                    : _createCommentVNode("", true)
                                ], 10, _hoisted_9))
                              : _createCommentVNode("", true),
                            (currentStep.value === _unref(BiddingSteps).TWICE)
                              ? (_openBlock(), _createElementBlock("button", {
                                  key: 4,
                                  class: _normalizeClass([
              'live-lot-button btn btn-rounded btn-blue-outline btn-click-effect',
              { disabled: liveLotBtnIsLoading.value || liveSettings.value?.is_complete_current_lot_auto }
            ]),
                                  disabled: liveLotBtnIsLoading.value || liveSettings.value?.is_complete_current_lot_auto,
                                  onClick: handleCompleteLot
                                }, [
                                  _createElementVNode("span", _hoisted_12, _toDisplayString(activeLot.value.bids_count > 0
                  ? _unref(trans)("live.action_buttons.sold_label")
                  : _unref(trans)("live.action_buttons.unsold_label")), 1),
                                  (liveSettings.value?.is_complete_current_lot_auto)
                                    ? (_openBlock(), _createElementBlock("span", {
                                        key: 0,
                                        class: "progress-bar",
                                        style: _normalizeStyle({ width: progressWidth.value + '%', backgroundColor: progressColor.value })
                                      }, null, 4))
                                    : _createCommentVNode("", true)
                                ], 10, _hoisted_11))
                              : _createCommentVNode("", true),
                            (currentStep.value === _unref(BiddingSteps).COMPLETE_LOT)
                              ? (_openBlock(), _createElementBlock("button", {
                                  key: 5,
                                  class: _normalizeClass([
              'live-lot-button btn btn-rounded btn-blue-outline btn-click-effect',
              { disabled: liveLotBtnIsLoading.value || liveSettings.value?.is_open_next_lot_auto }
            ]),
                                  disabled: liveLotBtnIsLoading.value || liveSettings.value?.is_open_next_lot_auto,
                                  onClick: handleNextLotBtn
                                }, [
                                  _createElementVNode("span", _hoisted_14, _toDisplayString(nextLotLabel.value), 1),
                                  (liveSettings.value?.is_open_next_lot_auto)
                                    ? (_openBlock(), _createElementBlock("span", {
                                        key: 0,
                                        class: "progress-bar",
                                        style: _normalizeStyle({ width: progressWidth.value + '%', backgroundColor: progressColor.value })
                                      }, null, 4))
                                    : _createCommentVNode("", true)
                                ], 10, _hoisted_13))
                              : _createCommentVNode("", true)
                          ], 64))
                    ], 64))
                  : (!isAuctionStarted.value)
                    ? (_openBlock(), _createElementBlock("button", {
                        key: 1,
                        class: _normalizeClass([
            'start-auction-button btn btn-rounded btn-blue-outline btn-click-effect',
             { disabled: liveSettings.value?.is_open_next_lot_auto }
          ]),
                        onClick: startAuction
                      }, _toDisplayString(_unref(trans)("live.action_buttons.start_auction")), 3))
                    : (_openBlock(), _createElementBlock("button", {
                        key: 2,
                        class: "start-auction-button btn btn-rounded btn-blue-outline btn-click-effect",
                        onClick: continueAuction
                      }, _toDisplayString(_unref(trans)("live.action_buttons.continue_auction")), 1))
              ], 64))
            : (lots.value.length === endedLots.value.length)
              ? (_openBlock(), _createElementBlock("button", {
                  key: 2,
                  class: _normalizeClass(["start-auction-button btn btn-rounded btn-blue-outline btn-click-effect", { disabled: auction.value.status === _unref(AuctionStatuses).ENDED }]),
                  onClick: endAuction
                }, _toDisplayString(auction.value.status === _unref(AuctionStatuses).ENDED
            ? _unref(trans)("live.action_buttons.auction_completed")
            : _unref(trans)("live.action_buttons.end_auction")), 3))
              : _createCommentVNode("", true)
        ]))
      : _createCommentVNode("", true),
    _createVNode(Popup, {
      class: "dashboard-popup",
      title: "Shortcuts",
      ref_key: "popup",
      ref: popup
    }, null, 512)
  ], 64))
}
}

})